import { h } from 'preact';
import { CartProductListItem } from './CartProductListItem';
import { useShopware } from '../shopware/ShopwareContext';
import type { LineItem } from '../shopware/shopwareApi';
import { buildGA4ItemFromLineItem, pushAddToCart, pushRemoveFromCart } from '../tracking/ecommerceDataLayer';

interface CartProductListProps {
  mode?: 'drawer' | 'page';
  showLegend?: boolean;
}

export const CartProductList = ({ mode = 'drawer', showLegend = false }: CartProductListProps) => {
  const { cart, updateQuantity, removeProduct, lastAddedProductId } = useShopware();

  if (!cart || !cart.lineItems || cart.lineItems.length === 0) {
    return null;
  }

  const handleRemoveItem = async (lineItem: LineItem) => {
    const ga4Item = buildGA4ItemFromLineItem(lineItem, lineItem.quantity || 1);
    try {
      await removeProduct(lineItem.id || '');
      if (ga4Item) {
        pushRemoveFromCart(ga4Item);
      }
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleQuantityChange = async (lineItem: LineItem, newQuantity: number) => {
    const currentQuantity = lineItem.quantity || 1;
    const quantityDelta = newQuantity - currentQuantity;

    try {
      await updateQuantity(lineItem.id || '', newQuantity);

      if (quantityDelta > 0) {
        const ga4Item = buildGA4ItemFromLineItem(lineItem, quantityDelta);
        if (ga4Item) {
          pushAddToCart(ga4Item);
        }
      } else if (quantityDelta < 0) {
        const ga4Item = buildGA4ItemFromLineItem(lineItem, Math.abs(quantityDelta));
        if (ga4Item) {
          pushRemoveFromCart(ga4Item);
        }
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const ulClassName =
    mode === 'page' ? 'shopping-cart-overview-items d-flex flex-column gap-2 mb-0' : 'd-flex flex-column gap-2';

  return (
    <>
      {showLegend && mode === 'page' && (
        <div
          className="d-none d-md-flex gap-4 align-items-center justify-content-between w-100 px-3 py-2 position-absolute bottom-100"
          aria-hidden="true"
        >
          <strong>
            <small id="legend-headline-product">Produkt</small>
          </strong>
          <strong className="d-flex gap-4">
            <small className="text-end" id="legend-headline-price">
              Stückpreis
            </small>
            <small className="text-center" id="legend-headline-amount">
              Menge
            </small>
            <small className="text-end me-5" id="legend-headline-total">
              Gesamt
            </small>
          </strong>
        </div>
      )}
      <ul
        aria-label="Produkte im Warenkorb"
        className={ulClassName}
        aria-labelledby={mode === 'page' ? 'shopping-cart-overview-headline' : undefined}
      >
        {cart.lineItems.map((lineItem: LineItem) => {
          // On mobile in drawer mode, only show the item that matches the last added product
          // Exception: always show promotion items
          const isLastAddedProduct = lineItem.referencedId === lastAddedProductId;
          const isPromotion = lineItem.type === 'promotion';
          const shouldHideOnMobile = mode === 'drawer' && !isLastAddedProduct && !isPromotion;

          return (
            <CartProductListItem
              key={lineItem.id}
              lineItem={lineItem}
              mode={mode}
              hideOnMobile={shouldHideOnMobile}
              onRemove={() => handleRemoveItem(lineItem)}
              onQuantityChange={(qty) => handleQuantityChange(lineItem, qty)}
            />
          );
        })}
      </ul>
    </>
  );
};
